/**
 * Agent → UI actions (protocol `ui.action`).
 *
 * - `navigate` / `open` route to the record or page (entity type → app route).
 * - `filter` / `highlight` go to whichever views registered a handler (CRM, pitches). With no handler,
 *   `highlight` flashes matching `[data-agent-entity="type:id"]` elements on screen, or routes to the record
 *   and flashes it there; `filter` routes to the page and is handed to the view's handler once it mounts.
 * - `insert_into_composer` goes to the composer registered for that draft / thread; the composer shows
 *   the inline diff and the creator accepts or rejects it.
 *
 * Views register while mounted (`useAgentActionHandler`, `useComposerTarget`).
 */
import { getCurrentInstance, onBeforeUnmount } from 'vue'
import type { ComposerEditTarget } from './protocol/types'
import type { UiAction } from './reducer'

export type ActionKind = UiAction['action']
type Handler = (target: Record<string, any>) => boolean | void

const handlers = new Map<ActionKind, Set<Handler>>()

export interface ComposerTarget {
    draftId?: string | null
    threadId?: string | null
    /** Show the proposal (inline diff). Returns false if this composer can't take it right now. */
    propose: (edit: ComposerEditTarget, key: string) => boolean
}

const composers = new Set<ComposerTarget>()

// An action that routed to a page with no handler yet: the page's handler takes it when it mounts.
const PENDING_MS = 10_000
let pending: { kind: ActionKind; target: Record<string, any>; at: number } | null = null

export function registerActionHandler(kind: ActionKind, fn: Handler): () => void {
    if (!handlers.has(kind)) handlers.set(kind, new Set())
    handlers.get(kind)!.add(fn)
    if (pending && pending.kind === kind && Date.now() - pending.at < PENDING_MS) {
        const p = pending
        queueMicrotask(() => {
            if (pending === p && fn(p.target) !== false) pending = null
        })
    }
    return () => handlers.get(kind)?.delete(fn)
}

export function useAgentActionHandler(kind: ActionKind, fn: Handler): void {
    const off = registerActionHandler(kind, fn)
    if (getCurrentInstance()) onBeforeUnmount(off)
}

// A composer edit that routed to its thread: the thread's composer takes it once it has the draft loaded.
let pendingEdit: { target: ComposerEditTarget; key: string; at: number } | null = null
const matches = (c: ComposerTarget, t: Pick<ComposerEditTarget, 'draft_id' | 'thread_id'>) =>
    Boolean((t.draft_id && c.draftId === t.draft_id) || (t.thread_id && c.threadId === t.thread_id))

export function registerComposer(c: ComposerTarget): () => void {
    composers.add(c)
    if (pendingEdit && Date.now() - pendingEdit.at < PENDING_MS) {
        const p = pendingEdit
        // The view registers during setup, before its draft has loaded: retry briefly.
        const attempt = (left: number) => {
            if (pendingEdit !== p || !composers.has(c)) return
            if (matches(c, p.target) && c.propose(p.target, p.key)) {
                pendingEdit = null
                return
            }
            if (left > 0) setTimeout(() => attempt(left - 1), 400)
        }
        queueMicrotask(() => attempt(8))
    }
    return () => composers.delete(c)
}

export function useComposerTarget(c: ComposerTarget): void {
    const off = registerComposer(c)
    if (getCurrentInstance()) onBeforeUnmount(off)
}

export function findComposer(t: Pick<ComposerEditTarget, 'draft_id' | 'thread_id'>): ComposerTarget | null {
    const list = [...composers].reverse() // newest first
    return list.find((c) => t.draft_id && c.draftId === t.draft_id) ?? list.find((c) => matches(c, t)) ?? null
}

const PAGE_ROUTES: Record<string, string> = {
    home: '/dashboard/home', inbox: '/dashboard/inbox', deals: '/dashboard/deals', crm: '/dashboard/deals',
    projects: '/dashboard/deals', pitches: '/dashboard/pitching', pitching: '/dashboard/pitching',
    companies: '/dashboard/companies', payments: '/dashboard/payments', invoices: '/dashboard/payments',
    calendar: '/dashboard/calendar', settings: '/dashboard/settings', mediakit: '/dashboard/mediakit',
    conversations: '/dashboard/conversations', queue: '/dashboard/conversations', review: '/dashboard/agent/review',
    jobs: '/dashboard/agent/jobs', goals: '/dashboard/settings/agent', memory: '/dashboard/settings/agent',
}

/** Where a `navigate`/`open` target lives in the app, or null if we don't know. */
export function routeFor(target: Record<string, any>): string | null {
    if (target.page) return PAGE_ROUTES[String(target.page)] ?? null
    const id = target.id ? encodeURIComponent(String(target.id)) : ''
    switch (target.type) {
        case 'project':
            return id ? `/dashboard/projects/${id}` : PAGE_ROUTES.deals
        case 'deliverable':
            return target.project_id && id
                ? `/dashboard/projects/${encodeURIComponent(target.project_id)}/deliverables/${id}`
                : PAGE_ROUTES.deals
        case 'thread':
        case 'email_thread':
        case 'message':
            return id ? `/dashboard/inbox?thread=${id}` : PAGE_ROUTES.inbox
        case 'draft':
            return target.thread_id ? `/dashboard/inbox?thread=${encodeURIComponent(target.thread_id)}` : PAGE_ROUTES.inbox
        case 'invoice':
            return id ? `/dashboard/invoices/${id}` : PAGE_ROUTES.payments
        case 'agreement':
        case 'contract':
            return id ? `/dashboard/agreements/${id}` : null
        case 'deal':
            return target.project_id ? `/dashboard/projects/${encodeURIComponent(target.project_id)}` : PAGE_ROUTES.deals
        case 'company':
            return id ? `/dashboard/companies?company=${id}` : PAGE_ROUTES.companies
        case 'pitch':
        case 'outreach':
            return PAGE_ROUTES.pitching
        case 'checkin':
        case 'task':
            return PAGE_ROUTES.home
        case 'goal':
            return PAGE_ROUTES.goals
        default:
            return null
    }
}

export interface DispatchDeps {
    push: (path: string) => unknown
}

/** Run one action. Returns what happened (for tests and the activity log). */
export function dispatchAction(a: UiAction, deps: DispatchDeps): 'routed' | 'handled' | 'composer' | 'unhandled' {
    if (a.action === 'insert_into_composer') {
        const target = a.target as ComposerEditTarget
        const key = String(target.call_id ?? a.eventId)
        const c = findComposer(target)
        if (c && c.propose(target, key)) return 'composer'
        // No composer for it on screen: open the thread; its composer picks the proposal up when it mounts.
        const path = routeFor({ type: 'draft', thread_id: target.thread_id })
        if (!path) return 'unhandled'
        pendingEdit = { target, key, at: Date.now() }
        deps.push(path)
        return 'routed'
    }
    for (const fn of [...(handlers.get(a.action) ?? [])].reverse()) {
        if (fn(a.target) !== false) return 'handled'
    }
    if (a.action === 'highlight' && highlightOnScreen(a.target)) return 'handled'
    const path = routeFor(a.target)
    if (!path) return 'unhandled'
    deps.push(path)
    if (a.action === 'filter') pending = { kind: 'filter', target: a.target, at: Date.now() }
    if (a.action === 'highlight') {
        // Flash it once the record's page has rendered.
        for (const ms of [400, 1200, 2500]) setTimeout(() => highlightOnScreen(a.target), ms)
    }
    return 'routed'
}

const FLASH_MS = 2600

/** Scroll to and flash elements tagged `data-agent-entity="type:id"`. True if any were on screen. */
export function highlightOnScreen(target: Record<string, any>): boolean {
    if (typeof document === 'undefined' || !target?.type) return false
    const ids: string[] = (Array.isArray(target.ids) ? target.ids : [target.id]).filter(Boolean).map(String)
    const want = new Set(ids.map((id) => `${target.type}:${id}`))
    const els = [...document.querySelectorAll<HTMLElement>('[data-agent-entity]')].filter((el) => want.has(el.dataset.agentEntity ?? ''))
    if (!els.length) return false
    els[0].scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    for (const el of els) {
        el.classList.remove('agent-highlight')
        void el.offsetWidth // restart the animation
        el.classList.add('agent-highlight')
        setTimeout(() => el.classList.remove('agent-highlight'), FLASH_MS)
    }
    return true
}

type ComposerOutcome = (key: string, accepted: boolean) => void
const outcomeListeners = new Set<ComposerOutcome>()

/** A composer reports what the creator did with a proposal; open agent surfaces update their notice. */
export function reportComposerEdit(key: string, accepted: boolean): void {
    for (const fn of outcomeListeners) fn(key, accepted)
}

export function onComposerEditResolved(fn: ComposerOutcome): () => void {
    outcomeListeners.add(fn)
    return () => outcomeListeners.delete(fn)
}

/** Tests only. */
export function _resetActions(): void {
    handlers.clear()
    composers.clear()
    pending = null
    pendingEdit = null
    outcomeListeners.clear()
}
